/**
 * One article.
 *
 * The page around the article is **content, not code**. An editor marks a Page as the
 * article layout (`articleLayoutFor`), stacks sections on it - an `article-body` where
 * the article itself goes, then whatever should follow: related articles, a news
 * teaser, a promo banner - and every article in that region and language is rendered
 * through it. `news` and `blog` can have their own layout, which is how the design's
 * `news_detail_page` and `blog_post_page` differ without two React components.
 *
 * With no layout page authored the article still renders, as a single `article-body`.
 * That is the same bargain the header makes when no Navigation entry exists: a fresh
 * install has to work before anyone has configured anything.
 */
import { useEffect, useMemo, useState } from 'react';
import ArticleBody from '../sections/custom/ArticleBody';
import SectionRenderer from '../sections/SectionRenderer';
import StatusPage from './StatusPage';
import { api, type ApiArticleDetail, type ApiPage } from '../lib/api';
import { mediaUrl } from '../lib/config';
import { applyHead, absoluteUrl } from '../lib/head';
import { CurrentArticleContext } from '../lib/article-context';
import { useSite } from '../lib/site-context';
import type { ArticleBodySection } from '../types/sections';

/** The layout used when no page has been marked as the article layout. */
const BARE_LAYOUT: ArticleBodySection = {
  __component: 'sections.article-body',
  type: 'articleBody',
  id: 0
};

export default function ArticlePage({ slug }: { slug: string }) {
  const { region, locale, pages, articlePath, t } = useSite();
  const [article, setArticle] = useState<ApiArticleDetail | null | 'loading'>('loading');
  const [layout, setLayout] = useState<ApiPage | null>(null);

  useEffect(() => {
    let cancelled = false;
    setArticle('loading');
    api.article(region.code, locale, slug)
      .then((res) => { if (!cancelled) setArticle(res); })
      .catch(() => { if (!cancelled) setArticle(null); });
    return () => { cancelled = true; };
  }, [region.code, locale, slug]);

  // A layout for this kind of article wins over one marked for all of them, so a blog
  // template does not have to repeat what the news template already says.
  const layoutSlug = useMemo(() => {
    if (!article || article === 'loading') return null;
    const candidates = pages.filter((p) => p.locale === locale && p.articleLayoutFor !== 'none');
    const match = candidates.find((p) => p.articleLayoutFor === article.kind)
      ?? candidates.find((p) => p.articleLayoutFor === 'all');
    return match?.slug ?? null;
  }, [article, pages, locale]);

  useEffect(() => {
    if (!layoutSlug) { setLayout(null); return; }
    let cancelled = false;
    api.page(region.code, locale, layoutSlug)
      .then((res) => { if (!cancelled) setLayout(res); })
      // A layout that cannot be loaded must not take the article down with it: fall
      // back to the bare body, which is still a readable page.
      .catch(() => { if (!cancelled) setLayout(null); });
    return () => { cancelled = true; };
  }, [region.code, locale, layoutSlug]);

  useEffect(() => {
    if (!article || article === 'loading') return;
    const canonical = absoluteUrl(region.domain, articlePath(article.slug));
    applyHead({
      title: article.seoTitle || article.title,
      description: article.seoDescription,
      canonical: canonical || undefined,
      ogImage: article.cover ? mediaUrl(article.cover.url) : undefined
    });
  }, [article, region.domain, articlePath]);

  if (article === 'loading') return <StatusPage title={t('page.loading')} body="" />;
  // Every genuine 404 lands here: DynamicPage falls through to an article when no page
  // matches, at any depth. So this is the not-found page, and has to say so.
  if (!article) return <StatusPage notFound title={t('page.notFound')} body={t('page.notFoundBody')} />;

  return (
    <CurrentArticleContext.Provider value={article}>
      {layout
        ? layout.sections.map((section) => (
            <SectionRenderer key={`${section.__component}-${section.id}`} section={section} />
          ))
        : <ArticleBody data={BARE_LAYOUT} />}
    </CurrentArticleContext.Provider>
  );
}
